import React, { useEffect, useMemo, useRef, useState } from "react";
import { dashboardApi } from "../../services/dashboard.api";
import type {
  EducationMapResponse,
  EducationMapSummary,
  ProvinceEducationSummary,
  ProvinceEducationDetailResponse,
} from "../../types/dashboard.types";
import styles from "./ProvinceLearningMap.module.scss";

const GEO_URL = new URL(
  "../../../../assets/geojsons-of-turkey/geojsons/tur_admin1.geojson",
  import.meta.url,
).href;

const WIDTH = 920;
const HEIGHT = 430;
const TRAININGS_PAGE_SIZE = 10;

interface ProvinceLearningStat {
  activeLearners: number;
  applications: number;
  averageSuccess: number;
  completedTrainings: number;
  completionRate?: number;
  ongoingTrainings: number;
  provinceId: string;
  provinceName: string;
  requestedTrainings: number;
  score?: number;
  totalTrainings: number;
  upcomingSessions: number;
}

interface TooltipState {
  stat: ProvinceLearningStat;
  x: number;
  y: number;
}

const normalizeProvinceName = (value: string) =>
  value
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/â/g, "a")
    .replace(/î/g, "i")
    .replace(/û/g, "u")
    .replace(/[^a-z0-9]/g, "");

const sanitizeProvinceDisplayName = (value: string) =>
  value.normalize("NFC").replace(/\u0307/g, "");

const getProvinceName = (feature: any): string =>
  sanitizeProvinceDisplayName(
    feature?.properties?.adm1_name1 || feature?.properties?.adm1_name || "",
  );

const formatNumber = (value: number) => value.toLocaleString("tr-TR");

const getCompletionRate = (stat: ProvinceLearningStat) => {
  if (Number.isFinite(stat.completionRate))
    return Math.round(stat.completionRate ?? 0);
  if (!stat.totalTrainings || stat.totalTrainings <= 0) return 0;
  return Math.round((stat.completedTrainings / stat.totalTrainings) * 100);
};

const toProvinceStat = (
  item: ProvinceEducationSummary,
): ProvinceLearningStat => ({
  activeLearners: item.activeLearners ?? 0,
  applications: item.applications ?? 0,
  averageSuccess: item.averageSuccessRate ?? 0,
  completedTrainings: item.completedTrainings ?? 0,
  completionRate: item.completionRate,
  ongoingTrainings: item.ongoingTrainings ?? 0,
  provinceId: item.provinceId,
  provinceName: sanitizeProvinceDisplayName(item.provinceName),
  requestedTrainings: item.requestedTrainings ?? 0,
  score: item.score,
  totalTrainings: item.totalTrainings ?? item.requestedTrainings ?? 1,
  upcomingSessions: item.upcomingTrainings ?? 0,
});

const EMPTY_SUMMARY: EducationMapSummary = {
  completedTrainings: 0,
  ongoingTrainings: 0,
  requestedTrainings: 0,
  upcomingTrainings: 0,
};

const getFillColor = (stat: ProvinceLearningStat, isSelected: boolean) => {
  if (isSelected) return "#9f151d";
  if (stat.averageSuccess >= 85) return "#e7212b";
  if (stat.averageSuccess >= 75) return "#fb3748";
  if (stat.averageSuccess >= 65) return "#ffac82";
  return "#ffe9d5";
};

const formatPoint = (point: [number, number]) =>
  `${Number(point[0].toFixed(3))},${Number(point[1].toFixed(3))}`;

type CoordinateBounds = {
  maxX: number;
  maxY: number;
  minX: number;
  minY: number;
};

type ProvinceProjection = (
  coordinate: [number, number],
) => null | [number, number];

const getCoordinateBounds = (features: any[]): CoordinateBounds | null => {
  let maxX = -Infinity;
  let maxY = -Infinity;
  let minX = Infinity;
  let minY = Infinity;

  const walk = (coordinates: unknown) => {
    if (Array.isArray(coordinates) && typeof coordinates[0] === "number") {
      const [x, y] = coordinates as [number, number];
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      return;
    }

    if (Array.isArray(coordinates)) {
      coordinates.forEach(walk);
    }
  };

  features.forEach((feature) => walk(feature?.geometry?.coordinates));

  if (![minX, maxX, minY, maxY].every(Number.isFinite)) return null;

  return { maxX, maxY, minX, minY };
};

const createProvinceProjection = (
  features: any[],
): ProvinceProjection | null => {
  const bounds = getCoordinateBounds(features);
  if (!bounds) return null;

  const padding = 34;
  const availableWidth = WIDTH - padding * 2;
  const availableHeight = HEIGHT - padding * 2;
  const scale = Math.min(
    availableWidth / (bounds.maxX - bounds.minX),
    availableHeight / (bounds.maxY - bounds.minY),
  );
  const mapWidth = (bounds.maxX - bounds.minX) * scale;
  const mapHeight = (bounds.maxY - bounds.minY) * scale;
  const offsetX = (WIDTH - mapWidth) / 2;
  const offsetY = (HEIGHT - mapHeight) / 2;

  return ([x, y]) => [
    offsetX + (x - bounds.minX) * scale,
    offsetY + (bounds.maxY - y) * scale,
  ];
};

const ringToPath = (
  ring: [number, number][],
  projection: ProvinceProjection,
) => {
  const points = ring
    .map((coordinate) => projection(coordinate))
    .filter((point): point is [number, number] => Array.isArray(point));

  if (points.length === 0) return "";

  return `M${points.map(formatPoint).join("L")}Z`;
};

const featureToPath = (
  feature: any,
  projection: ProvinceProjection,
): string => {
  const geometry = feature?.geometry;
  if (!geometry) return "";

  if (geometry.type === "Polygon") {
    return (geometry.coordinates ?? [])
      .map((ring: [number, number][]) => ringToPath(ring, projection))
      .join("");
  }

  if (geometry.type === "MultiPolygon") {
    return (geometry.coordinates ?? [])
      .flatMap((polygon: [number, number][][]) => polygon)
      .map((ring: [number, number][]) => ringToPath(ring, projection))
      .join("");
  }

  return "";
};

const getProvinceCode = (provinceName: string) =>
  sanitizeProvinceDisplayName(provinceName)
    .toLocaleUpperCase("tr-TR")
    .replace(/İ/g, "I")
    .replace(/Ğ/g, "G")
    .replace(/Ü/g, "U")
    .replace(/Ş/g, "S")
    .replace(/Ö/g, "O")
    .replace(/Ç/g, "C")
    .slice(0, 3);

const BIG_CITY_NAMES = ["İstanbul", "Ankara", "İzmir"];

const ProvinceLearningMap: React.FC = () => {
  const detailRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledProvinceRef = useRef<null | string>(null);
  const [features, setFeatures] = useState<any[]>([]);
  const [hover, setHover] = useState<TooltipState | null>(null);
  const [mapData, setMapData] = useState<EducationMapResponse | null>(null);
  const [provinceDetail, setProvinceDetail] =
    useState<ProvinceEducationDetailResponse | null>(null);
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [selectedProvinceRequestId, setSelectedProvinceRequestId] = useState<
    string | null
  >(null);
  const [trainingPage, setTrainingPage] = useState(1);

  useEffect(() => {
    let cancelled = false;

    fetch(GEO_URL)
      .then((response) => response.json())
      .then((geojson) => {
        if (cancelled) return;
        setFeatures(
          (geojson?.features ?? []).filter((feature: any) => feature?.geometry),
        );
      })
      .catch(() => setFeatures([]));

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    dashboardApi
      .getEducationMap()
      .then((data) => {
        if (cancelled) return;
        setMapData(data);
      })
      .catch(() => {
        if (cancelled) return;
        setMapData({
          bigCities: [],
          featuredRegions: [],
          provinces: [],
          summary: EMPTY_SUMMARY,
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const statsByProvince = useMemo(() => {
    const map: Record<string, ProvinceLearningStat> = {};
    (mapData?.provinces ?? []).forEach((province) => {
      const stat = toProvinceStat(province);
      map[normalizeProvinceName(stat.provinceName)] = stat;
    });
    return map;
  }, [mapData]);

  const projection = useMemo(() => {
    if (features.length === 0) return null;
    return createProvinceProjection(features);
  }, [features]);

  const provincePaths = useMemo(() => {
    if (!projection) return [];

    return features
      .map((feature) => ({
        d: featureToPath(feature, projection),
        provinceName: getProvinceName(feature),
      }))
      .filter((item) => item.d && item.provinceName);
  }, [features, projection]);

  const provinceLabels = useMemo(() => {
    if (!projection) return [];

    return features
      .map((feature) => {
        const provinceName = getProvinceName(feature);
        const lon = Number(feature?.properties?.center_lon);
        const lat = Number(feature?.properties?.center_lat);
        if (!provinceName || !Number.isFinite(lon) || !Number.isFinite(lat)) {
          return null;
        }
        const point = projection([lon, lat]);
        if (!point) return null;

        return {
          code: getProvinceCode(provinceName),
          provinceName,
          x: point[0],
          y: point[1],
        };
      })
      .filter(Boolean) as Array<{
      code: string;
      provinceName: string;
      x: number;
      y: number;
    }>;
  }, [features, projection]);

  const baseSelectedStat = selectedProvince
    ? statsByProvince[normalizeProvinceName(selectedProvince)]
    : null;
  const selectProvince = (
    provinceName: string,
    stat?: ProvinceLearningStat | null,
  ) => {
    setSelectedProvince(provinceName);
    setSelectedProvinceRequestId(stat?.provinceId ?? provinceName);
  };

  const clearSelectedProvince = () => {
    setSelectedProvince(null);
    setSelectedProvinceRequestId(null);
  };

  const selectedStat = provinceDetail?.province
    ? toProvinceStat(provinceDetail.province)
    : baseSelectedStat;

  const selectedTrainings = provinceDetail?.trainings?.data ?? [];
  const selectedTrainingsTotal =
    provinceDetail?.trainings?.total ?? selectedTrainings.length;
  const trainingPageCount =
    provinceDetail?.trainings?.totalPages ??
    Math.ceil(selectedTrainingsTotal / TRAININGS_PAGE_SIZE);
  const paginatedTrainings = selectedTrainings;

  useEffect(() => {
    setTrainingPage(1);
    setProvinceDetail(null);
  }, [selectedProvince]);

  useEffect(() => {
    if (!selectedProvince || !selectedProvinceRequestId) return;
    let cancelled = false;

    dashboardApi
      .getEducationMapProvince(selectedProvinceRequestId, {
        page: trainingPage,
        pageSize: TRAININGS_PAGE_SIZE,
      })
      .then((data) => {
        if (cancelled) return;
        setProvinceDetail(data);
      })
      .catch(() => {
        if (cancelled) return;
        if (!baseSelectedStat) {
          setProvinceDetail(null);
          return;
        }
        setProvinceDetail({
          province: {
            activeLearners: baseSelectedStat.activeLearners,
            applications: baseSelectedStat.applications,
            averageSuccessRate: baseSelectedStat.averageSuccess,
            completedTrainings: baseSelectedStat.completedTrainings,
            ongoingTrainings: baseSelectedStat.ongoingTrainings,
            provinceId: baseSelectedStat.provinceId,
            provinceName: baseSelectedStat.provinceName,
            requestedTrainings: baseSelectedStat.requestedTrainings,
            totalTrainings: baseSelectedStat.totalTrainings,
            upcomingTrainings: baseSelectedStat.upcomingSessions,
          },
          trainings: {
            data: [],
            page: trainingPage,
            pageSize: TRAININGS_PAGE_SIZE,
            total: 0,
            totalPages: 0,
          },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [baseSelectedStat, selectedProvince, selectedProvinceRequestId, trainingPage]);

  useEffect(() => {
    if (!selectedProvince) {
      lastScrolledProvinceRef.current = null;
      return;
    }
    if (lastScrolledProvinceRef.current === selectedProvince) return;

    lastScrolledProvinceRef.current = selectedProvince;
    window.setTimeout(() => {
      detailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 50);
  }, [selectedProvince]);

  const totals = useMemo(() => {
    const summary = mapData?.summary ?? EMPTY_SUMMARY;

    return {
      completedTrainings: summary.completedTrainings ?? 0,
      ongoingTrainings: summary.ongoingTrainings ?? 0,
      requestedTrainings: summary.requestedTrainings ?? 0,
      upcomingSessions: summary.upcomingTrainings ?? 0,
    };
  }, [mapData]);

  const bigCityStats = useMemo(() => {
    const apiBigCities = (mapData?.bigCities ?? []).map(toProvinceStat);
    if (apiBigCities.length > 0) return apiBigCities;

    return BIG_CITY_NAMES.map(
      (cityName) => statsByProvince[normalizeProvinceName(cityName)],
    ).filter(Boolean) as ProvinceLearningStat[];
  }, [mapData, statsByProvince]);

  const featuredRegions = useMemo(() => {
    const source =
      mapData?.featuredRegions && mapData.featuredRegions.length > 0
        ? mapData.featuredRegions.map(toProvinceStat)
        : Object.values(statsByProvince);

    return source
      .map((item) => ({
        ...item,
        completionRate: getCompletionRate(item),
        engagementScore:
          item.score ??
          Math.round(
            item.averageSuccess * 0.45 +
              Math.min(item.activeLearners / 8, 100) * 0.35 +
              Math.min(item.applications / 18, 100) * 0.2,
          ),
      }))
      .sort((a, b) => b.engagementScore - a.engagementScore)
      .slice(0, 2);
  }, [mapData, statsByProvince]);

  const nationalChartTotal = Math.max(
    1,
    totals.requestedTrainings +
      totals.completedTrainings +
      totals.ongoingTrainings +
      totals.upcomingSessions,
  );
  const nationalChartSlices = [
    {
      className: styles.donutRequested,
      color: "#e7212b",
      label: "Talep edilen",
      value: totals.requestedTrainings,
    },
    {
      className: styles.donutCompleted,
      color: "#22c55e",
      label: "Tamamlanan",
      value: totals.completedTrainings,
    },
    {
      className: styles.donutOngoing,
      color: "#f59e0b",
      label: "Devam eden",
      value: totals.ongoingTrainings,
    },
    {
      className: styles.donutUpcoming,
      color: "#72808f",
      label: "Yaklaşan",
      value: totals.upcomingSessions,
    },
  ];
  let donutOffset = 0;
  const donutStops = nationalChartSlices
    .map((slice) => {
      const start = donutOffset;
      const end = start + (slice.value / nationalChartTotal) * 100;
      donutOffset = end;
      return `${slice.color} ${start}% ${end}%`;
    })
    .join(", ");

  return (
    <section className={styles.panel}>
      <div className={styles.panelHeader}>
        <div>
          <h4 className={styles.heading}>Türkiye Geneli Eğitim Dağılımı</h4>
        </div>
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <span>Talep edilen</span>
            <strong>{formatNumber(totals.requestedTrainings)}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>Tamamlanan</span>
            <strong>{formatNumber(totals.completedTrainings)}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>Devam eden</span>
            <strong>{formatNumber(totals.ongoingTrainings)}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span>Yaklaşan</span>
            <strong>{formatNumber(totals.upcomingSessions)}</strong>
          </div>
        </div>
      </div>

      {bigCityStats.length > 0 && (
        <div className={styles.bigCityGrid}>
          {bigCityStats.map((city) => {
            const completionRate = getCompletionRate(city);

            return (
              <button
                key={city.provinceName}
                type="button"
                className={styles.bigCityCard}
                onClick={() => selectProvince(city.provinceName, city)}
              >
                <div className={styles.bigCityCardHeader}>
                  <div>
                    <h5>{city.provinceName}</h5>
                  </div>
                  <strong>%{city.averageSuccess}</strong>
                </div>
                <div className={styles.bigCityMetrics}>
                  <div>
                    <span>Talep edilen</span>
                    <strong>{formatNumber(city.requestedTrainings)}</strong>
                  </div>
                  <div>
                    <span>Tamamlanan</span>
                    <strong>{formatNumber(city.completedTrainings)}</strong>
                  </div>
                  <div>
                    <span>Devam eden</span>
                    <strong>{formatNumber(city.ongoingTrainings)}</strong>
                  </div>
                  <div>
                    <span>Yaklaşan</span>
                    <strong>{formatNumber(city.upcomingSessions)}</strong>
                  </div>
                </div>
                <div className={styles.bigCityProgress}>
                  <span>Tamamlanma oranı</span>
                  <strong>%{completionRate}</strong>
                  <i style={{ width: `${Math.min(completionRate, 100)}%` }} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className={styles.contentGrid}>
        <aside className={styles.sideCard}>
          <div className={styles.sideHeader}>
            <div>
              <h5>Öne çıkan bölgeler</h5>
              <p>Katılım, başvuru ve başarı skoruna göre ilk 2 il.</p>
            </div>
          </div>
          <div className={styles.featuredList}>
            {featuredRegions.map((item, index) => {
              const completionRate = getCompletionRate(item);

              return (
                <button
                  key={item.provinceName}
                  type="button"
                  className={styles.featuredRow}
                  onClick={() => selectProvince(item.provinceName, item)}
                >
                  <span className={styles.featuredRank}>{index + 1}</span>
                  <span className={styles.featuredBody}>
                    <span className={styles.featuredTopline}>
                      <strong>{item.provinceName}</strong>
                      <em>%{item.averageSuccess}</em>
                    </span>
                    <span className={styles.featuredMetricsGrid}>
                      <span>
                        <small>Talep</small>
                        <b>{formatNumber(item.requestedTrainings)}</b>
                      </span>
                      <span>
                        <small>Tamam</small>
                        <b>{formatNumber(item.completedTrainings)}</b>
                      </span>
                      <span>
                        <small>Devam</small>
                        <b>{formatNumber(item.ongoingTrainings)}</b>
                      </span>
                      <span>
                        <small>Yaklaşan</small>
                        <b>{formatNumber(item.upcomingSessions)}</b>
                      </span>
                    </span>
                    <span className={styles.featuredCompletion}>
                      <span>Tamamlanma oranı</span>
                      <strong>%{completionRate}</strong>
                      <i
                        style={{ width: `${Math.min(completionRate, 100)}%` }}
                      />
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className={styles.sideChartCard}>
            <div className={styles.sideChartHeader}>
              <h6>Türkiye geneli eğitim durumu</h6>
              <span>Toplam dağılım</span>
            </div>
            <div className={styles.donutWrap}>
              <div
                className={styles.donutChart}
                style={{ background: `conic-gradient(${donutStops})` }}
              >
                <div>
                  <strong>{formatNumber(totals.requestedTrainings)}</strong>
                  <span>talep</span>
                </div>
              </div>
              <div className={styles.donutLegend}>
                {nationalChartSlices.map((slice) => (
                  <div key={slice.label}>
                    <span>
                      <i className={slice.className} /> {slice.label}
                    </span>
                    <strong>{formatNumber(slice.value)}</strong>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <div className={styles.mapCard}>
          <div className={styles.mapToolbar}>
            <span>İl seçmek için haritaya tıklayın.</span>
            {selectedProvince && (
              <button type="button" onClick={clearSelectedProvince}>
                Seçimi temizle
              </button>
            )}
          </div>
          <div className={styles.mapWrap} onMouseLeave={() => setHover(null)}>
            {projection ? (
              <svg
                className={styles.mapSvg}
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                role="img"
              >
                <title>Türkiye il bazlı LMS istatistik haritası</title>
                {provincePaths.map(({ d, provinceName }) => {
                  const stat =
                    statsByProvince[normalizeProvinceName(provinceName)];
                  const isSelected = selectedProvince === provinceName;

                  return (
                    <path
                      key={provinceName}
                      aria-label={provinceName}
                      className={styles.province}
                      d={d}
                      fill={
                        stat
                          ? getFillColor(stat, isSelected)
                          : isSelected
                            ? "#ffac82"
                            : "#dee2e6"
                      }
                      data-selected={isSelected ? "true" : "false"}
                      onClick={() => selectProvince(provinceName, stat)}
                      onMouseMove={(event) => {
                        if (!stat) return;
                        const rect =
                          event.currentTarget.ownerSVGElement?.getBoundingClientRect();
                        setHover({
                          stat,
                          x: event.clientX - (rect?.left ?? 0),
                          y: event.clientY - (rect?.top ?? 0),
                        });
                      }}
                    />
                  );
                })}
                {provinceLabels.map((label) => (
                  <text
                    key={`${label.provinceName}-label`}
                    className={styles.provinceLabel}
                    x={label.x}
                    y={label.y}
                  >
                    {label.code}
                  </text>
                ))}
              </svg>
            ) : (
              <div className={styles.loading}>Harita yükleniyor…</div>
            )}

            {hover && (
              <div
                className={styles.tooltip}
                style={{ left: hover.x + 14, top: hover.y + 14 }}
              >
                <strong>{hover.stat.provinceName}</strong>
                <span>
                  Aktif kullanıcı: {formatNumber(hover.stat.activeLearners)}
                </span>
                <span>
                  Tamamlanan eğitim:{" "}
                  {formatNumber(hover.stat.completedTrainings)}
                </span>
                <span>Başarı oranı: %{hover.stat.averageSuccess}</span>
              </div>
            )}
          </div>
          <div className={styles.legend}>
            <span>
              <i className={styles.legendLow} /> %65 altı
            </span>
            <span>
              <i className={styles.legendMid} /> %65 - %74
            </span>
            <span>
              <i className={styles.legendHigh} /> %75 - %84
            </span>
            <span>
              <i className={styles.legendBest} /> %85+
            </span>
          </div>
        </div>
      </div>

      {selectedStat && (
        <div className={styles.detailCard} ref={detailRef}>
          <div className={styles.detailHeader}>
            <div>
              <h4>{selectedStat.provinceName} Eğitim Özeti</h4>
              <p>Şehir genelindeki eğitim talepleri ve devam eden süreçler.</p>
            </div>
          </div>
          <div className={styles.detailGrid}>
            <div>
              <span>Talep edilen</span>
              <strong>{formatNumber(selectedStat.requestedTrainings)}</strong>
            </div>
            <div>
              <span>Tamamlanan</span>
              <strong>{formatNumber(selectedStat.completedTrainings)}</strong>
            </div>
            <div>
              <span>Devam eden</span>
              <strong>{formatNumber(selectedStat.ongoingTrainings)}</strong>
            </div>
            <div>
              <span>Yaklaşan</span>
              <strong>{formatNumber(selectedStat.upcomingSessions)}</strong>
            </div>
          </div>

          <div className={styles.trainingSectionHeader}>
            <div>
              <h5>Şehirdeki eğitimler</h5>
              <p>Her kart seçili ildeki eğitime ait istatistikleri gösterir.</p>
            </div>
            <span>{selectedTrainingsTotal} eğitim</span>
          </div>

          {paginatedTrainings.length > 0 ? (
            <div className={styles.trainingGrid}>
              {paginatedTrainings.map((training) => (
                <article
                  className={styles.trainingCard}
                  key={training.educationId}
                >
                  <div className={styles.trainingCardHeader}>
                    <div>
                      <span>{training.category ?? "Eğitim"}</span>
                      <h6>{training.title}</h6>
                    </div>
                    <strong>%{training.successRate}</strong>
                  </div>
                  <div className={styles.trainingMetrics}>
                    <div>
                      <span>Talep eden</span>
                      <strong>{formatNumber(training.requestedUsers)}</strong>
                    </div>
                    <div>
                      <span>Aktif kullanıcı</span>
                      <strong>{formatNumber(training.activeUsers)}</strong>
                    </div>
                    <div>
                      <span>Tamamlayan</span>
                      <strong>{formatNumber(training.completedUsers)}</strong>
                    </div>
                    <div>
                      <span>Başarı oranı</span>
                      <strong>%{training.successRate}</strong>
                    </div>
                    <div>
                      <span>Onaylanan</span>
                      <strong>{formatNumber(training.approvedUsers)}</strong>
                    </div>
                    <div>
                      <span>Reddedilen</span>
                      <strong>{formatNumber(training.rejectedUsers)}</strong>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className={styles.trainingEmpty}>
              Bu il için eğitim bulunmuyor.
            </div>
          )}

          {trainingPageCount > 1 && (
            <div className={styles.trainingPagination}>
              <button
                type="button"
                disabled={trainingPage === 1}
                onClick={() => setTrainingPage((page) => Math.max(1, page - 1))}
              >
                Önceki
              </button>
              <span>
                {trainingPage} / {trainingPageCount}
              </span>
              <button
                type="button"
                disabled={trainingPage === trainingPageCount}
                onClick={() =>
                  setTrainingPage((page) =>
                    Math.min(trainingPageCount, page + 1),
                  )
                }
              >
                Sonraki
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ProvinceLearningMap;
